import { API } from '../api';
import {
  FetchPostByUserIdParams,
  FetchPostParams,
  FetchPostVideoSuggestionParams,
  ICommentResponse,
  IFetchCommentParams,
  IFetchReplyCommentParams,
  IPostResponse,
  IReplyCommentParams,
  LikeCommentParams,
  IReplyCommentResponse,
  IPostModel,
  IBookmarkResponse,
} from '../models';
import { IHashTag, ISearchHashtagPayload } from '../types/hashtag';

const endPoint = {
  homePost: 'v1/post-service/posts/home-post',
  publicHomePost: 'v1/post-service/public/home-post',
  reelsFeed: 'v1/post-service/posts/reels-feed',
  increaseViewCount: 'v1/post-service/posts/increase-view-count',
  postDetail: 'v1/post-service/posts',
  postsByUser: 'v1/post-service/posts/get-posts-by-user',
  homeComment: 'v1/post-service/comments/get-comment-by-postId',
  publicHomeComment: 'v1/post-service/public/get-comment-by-postId',
  homeReplyComment: 'v1/post-service/comments/get-reply-by-commentId',
  homeCreateReplyComment: 'v1/post-service/comments/reply',
  likeComment: 'v1/post-service/comments/like-toggle',
  homeLikePost: 'v1/post-service/posts/post/up',
  homeDisLikePost: 'v1/post-service/posts/post/down',
  hashTag: 'v1/post-service/posts/get-hashtag',
  publicHashTag: 'v1/post-service/public/get-hashtag',
  searchHashTag: 'v1/post-service/posts/search-hashtag',
  postsTypeVideo: 'v1/post-service/postvideos/post-videos',
  postsVideoSuggest: 'v1/post-service/posts/get-videos-suggestion',
  deletePost: 'v1/post-service/posts',
  getBookmark: '/v1/post-service/posts/bookmarks/get',
  bookmark: '/v1/post-service/posts/bookmarks',
  getPostByHashtag: 'v1/post-service/posts/get-posts-by-hashtag',
  getPostByCategory: 'v1/post-service/posts/get-posts-by-category',
  sharePost: '/v1/post-service/posts/shares',
  sharePostUsers: '/v1/post-service/posts/shares-post-users',
  getPostByShortId: 'v1/post-service/posts/get-post-by-short-id',
  getPostUncheck: '/v1/post-service/posts/get-post-uncheck',
  editComment: 'v1/post-service/comments/edit-comment',
  deleteComment: 'v1/post-service/comments/delete-comment',
  confirmShow: 'v1/user-service/users/update-post-alert-preference',
  listAction: '/v1/post-service/posts/get-interacted-users',
};

export const postService = {
  async getPosts(payload: FetchPostParams) {
    const { data } = await API.post<IPostResponse>(endPoint.homePost, payload);
    return data;
  },
  async getPublicPosts(payload: FetchPostParams) {
    const { data } = await API.post<IPostResponse>(
      endPoint.publicHomePost,
      payload
    );
    return data;
  },
  // Reels: feed trộn video+post đã rank + đẩy video lên (server). Chỉ cho user đã đăng nhập.
  async getReelsFeed(payload: FetchPostParams) {
    const { data } = await API.post<IPostResponse>(endPoint.reelsFeed, payload);
    return data;
  },
  async increaseViewCount(postId: string) {
    await API.put(endPoint.increaseViewCount, { postId });
  },
  async getPostUncheck(payload: FetchPostParams) {
    const { data } = await API.post<IPostResponse>(
      endPoint.getPostUncheck,
      payload
    );
    return data;
  },
  async getDetailPost(postId: string) {
    const { data } = await API.get<IPostModel>(
      `${endPoint.postDetail}/${postId}`
    );
    return data;
  },

  async getPostByShortId(shortId: string) {
    const { data } = await API.get<IPostModel>(
      `${endPoint.getPostByShortId}/${shortId}`
    );
    return data;
  },

  async getPostsTypeVideo(payload: FetchPostParams) {
    const { data } = await API.post<IPostResponse>(
      endPoint.postsTypeVideo,
      payload
    );
    return data;
  },
  async getPostsVideoSuggestion(payload: FetchPostVideoSuggestionParams) {
    const { data } = await API.post<IPostResponse>(
      endPoint.postsVideoSuggest,
      payload
    );
    return data;
  },
  async getPostsByUser(payload: FetchPostByUserIdParams) {
    const { data } = await API.post<IPostResponse>(
      endPoint.postsByUser,
      payload
    );
    return data;
  },

  async getCommentByPostId(payload: IFetchCommentParams) {
    const { data } = await API.post<ICommentResponse>(
      endPoint.homeComment,
      payload
    );
    return data;
  },

  async getPublicCommentByPostId(payload: IFetchCommentParams) {
    const { data } = await API.post<ICommentResponse>(
      endPoint.publicHomeComment,
      payload
    );
    return data;
  },

  async getReplyCommentByCommentId(payload: IFetchReplyCommentParams) {
    const { data } = await API.post<IReplyCommentResponse>(
      endPoint.homeReplyComment,
      payload
    );
    return data;
  },

  async replyComment(payload: IReplyCommentParams) {
    const { data } = await API.post<IReplyCommentResponse>(
      endPoint.homeCreateReplyComment,
      payload
    );
    return data;
  },

  async likeComment(payload: LikeCommentParams) {
    const { data } = await API.put<LikeCommentParams>(
      endPoint.likeComment,
      payload
    );
    return data;
  },

  async likePost(postId: string) {
    const { data } = await API.put<IReplyCommentResponse>(
      endPoint.homeLikePost,
      { postId }
    );
    return data;
  },

  async diLikePost(postId: string) {
    const { data } = await API.put<IReplyCommentResponse>(
      endPoint.homeDisLikePost,
      { postId }
    );
    return data;
  },

  async getHashTag(total?: number) {
    const url = `${endPoint.hashTag}/${total || 10}`;
    const { data } = await API.get<IHashTag[]>(url);
    return data;
  },

  async getPublicHashTag(total?: number) {
    const url = `${endPoint.publicHashTag}/${total || 10}`;
    const { data } = await API.get<IHashTag[]>(url);
    return data;
  },

  async searchHashTag(payload: ISearchHashtagPayload) {
    const { data } = await API.post<IHashTag[]>(
      endPoint.searchHashTag,
      payload
    );
    return data;
  },

  async deletePost(postId?: string) {
    const { data } = await API.delete(`${endPoint.deletePost}/${postId}`);
    return data;
  },

  async getBookmark(body: {
    userId: string;
    pageSize: number;
    pageIndex: number;
  }) {
    const { data } = await API.post<IBookmarkResponse>(
      endPoint.getBookmark,
      body
    );
    return data;
  },

  async toggleBookmark(body: { postId: string }) {
    const { data } = await API.post<boolean>(endPoint.bookmark, body);
    return data;
  },

  async getPostByHashtag(body: {
    userId: string;
    hashTag: string;
    pageNumber: number;
    pageSize: number;
  }) {
    const { data } = await API.post<IPostResponse>(
      endPoint.getPostByHashtag,
      body
    );
    return data;
  },

  async getPostByCategory(body: {
    userId: string;
    categoryId: string;
    pageNumber: number;
    pageSize: number;
  }) {
    const { data } = await API.post<IPostResponse>(
      endPoint.getPostByCategory,
      body
    );
    return data;
  },

  async sharePost(body: {
    userId: string;
    postId: string;
    shareLink: string;
    isPublic: boolean;
    audienceType: number;
    content: string;
  }) {
    const { data } = await API.post<any>(endPoint.sharePost, body);
    return data;
  },

  async sharePostUsers(body: {
    userId: string;
    postId: string;
    shareLink: string;
    sharedUserIds: string[];
    isPublic: boolean;
    audienceType: number;
  }) {
    const { data } = await API.post<any>(endPoint.sharePostUsers, body);
    return data;
  },
  async editComment(payload: {
    id: string;
    commentType: number;
    content: string;
    imageId: null;
  }) {
    const { data } = await API.put(endPoint.editComment, payload);
    return data;
  },
  async deleteComment(commentId: string, commentType: number = 0) {
    const { data } = await API.post(endPoint.deleteComment, {
      id: commentId,
      commentType,
    });
    return data;
  },

  async confirmShow(isSuppressed: boolean, suppressDays: number | null) {
    const { data } = await API.post(endPoint.confirmShow, {
      isSuppressed,
      suppressDays,
    });
    return data;
  },

  async listAction(
    postId: string,
    type: number = 1,
    pageNumber: number = 1,
    pageSize: number = 10
  ) {
    const { data } = await API.post(endPoint.listAction, {
      postId,
      type,
      pageNumber,
      pageSize,
    });
    return data;
  },
};
